import { useQuery } from '@apollo/client';
import { READ_CHARGING_SESSION } from 'graphql/queries/readChargingSession';
import { READ_PARKING_SESSION_RETRY_STATUS } from 'graphql/queries/readParkingSessionRetryStatus';
import React, { useEffect, useRef } from 'react';
import {
  Query,
  QueryReadChargingSessionArgs,
  QueryReadParkingSessionArgs,
} from 'types/generatedSchemaTypes';
import { useRetryPaymentStore } from '../stores/retryPaymentStore';
import useIsAccountRestricted from './useIsAccountRestricted';

const PARKING_POLL_INTERVAL = 3000;
const CHARGING_POLL_INTERVAL = 1000;

const RetryPaymentSessionPoller: React.FC<{ sessionId: string }> = ({
  sessionId,
}) => {
  const { startPolling, stopPolling } = useQuery<
    Query,
    QueryReadParkingSessionArgs
  >(READ_PARKING_SESSION_RETRY_STATUS, {
    variables: { sessionId },
    fetchPolicy: 'no-cache',
    skip: !sessionId,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const paymentState = data?.readParkingSession?.paymentState;

      const { setSessionStatus } = useRetryPaymentStore.getState();

      if (paymentState === 'Retrying' || paymentState === 'Pending') {
        return;
      }

      if (paymentState === 'Success') {
        setSessionStatus(sessionId, 'Success');
        stopPolling();
      } else if (paymentState === 'Failed') {
        setSessionStatus(sessionId, 'Failed');
        stopPolling();
      }
    },
  });

  useEffect(() => {
    if (!sessionId) return;
    startPolling(PARKING_POLL_INTERVAL);
    return () => stopPolling();
  }, [sessionId, startPolling, stopPolling]);

  return null;
};

const RetryChargingPaymentSessionPoller: React.FC<{
  sessionId: string;
}> = ({ sessionId }) => {
  const { startPolling, stopPolling } = useQuery<
    Query,
    QueryReadChargingSessionArgs
  >(READ_CHARGING_SESSION, {
    variables: { sessionId },
    fetchPolicy: 'no-cache',
    skip: !sessionId,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const paymentState = data?.readChargingSession?.paymentState as
        | string
        | null
        | undefined;

      const { setChargingSessionStatus } = useRetryPaymentStore.getState();

      if (
        paymentState === 'Retrying' ||
        paymentState === 'Pending' ||
        paymentState === 'NotInitiated'
      ) {
        return;
      }

      if (paymentState === 'Success') {
        setChargingSessionStatus(sessionId, 'Success');
        stopPolling();
      } else if (paymentState === 'Failed') {
        setChargingSessionStatus(sessionId, 'Failed');
        stopPolling();
      }
    },
  });

  useEffect(() => {
    if (!sessionId) return;
    startPolling(CHARGING_POLL_INTERVAL);
    return () => {
      stopPolling();
    };
  }, [sessionId, startPolling, stopPolling]);

  return null;
};

export const RetryPaymentPolling: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const pollingIds = useRetryPaymentStore((s) => s.getRetryingSessions());
  const chargingPollingIds = useRetryPaymentStore((s) =>
    s.getRetryingChargingSessions()
  );
  const successfulPaymentCount = useRetryPaymentStore(
    (s) => s.successfulPaymentCount
  );

  // Refresh the account-restricted gate here after a successful retry, not in
  // the pollers. A poller disappears as soon as its payment succeeds, so a
  // refetch started there is cancelled before it can update accountRestricted.
  const { refetchFailedSessions } = useIsAccountRestricted();
  const refetchFailedSessionsRef = useRef(refetchFailedSessions);
  refetchFailedSessionsRef.current = refetchFailedSessions;

  useEffect(() => {
    if (successfulPaymentCount > 0) {
      refetchFailedSessionsRef.current();
    }
  }, [successfulPaymentCount]);

  return (
    <>
      {children}
      {pollingIds.map((sessionId) => (
        <RetryPaymentSessionPoller key={sessionId} sessionId={sessionId} />
      ))}
      {chargingPollingIds.map((sessionId) => (
        <RetryChargingPaymentSessionPoller
          key={sessionId}
          sessionId={sessionId}
        />
      ))}
    </>
  );
};
